/**
 * TenantPlacement — landlord dashboard to publish vacant units to the
 * Central Rentals public directory and get enquiries fast.
 * Route: /tenant-placement (subscriber-only).
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Rocket, Loader2, ArrowRight, Eye, MessageCircle, TrendingUp, Trash2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui/Primitives";
import { usePageTitle } from "@/lib/seo";
import { toast } from "sonner";
import { PlacementPipeline } from "@/components/placement/PlacementPipeline";
import { Button } from "@/components/ui/button";

export default function TenantPlacement() {
  usePageTitle("Tenant Placement — Central Rentals Canada");
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "pipeline" ? "pipeline" : "listings");
  const [state, setState] = useState({ published: [], vacant_candidates: [] });
  const [loading, setLoading] = useState(true);
  const [paywalled, setPaywalled] = useState(false);
  const [publishUnitId, setPublishUnitId] = useState(params.get("unit") || "");
  const [form, setForm] = useState({
    headline: "",
    description: "",
    rent_cad: "",
    bedrooms: "",
    bathrooms: "",
    pets: "",
    available_on: "",
    address_public: "",
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/tenant-placement/mine");
      setState(data || { published: [], vacant_candidates: [] });
    } catch (e) {
      if (e?.response?.status === 402) setPaywalled(true);
      else toast.error("Couldn't load your placement dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openPublishFor = (unitId) => {
    setPublishUnitId(unitId);
    const candidate = state.vacant_candidates.find((v) => v.unit_id === unitId);
    if (candidate) {
      setForm((f) => ({
        ...f,
        bedrooms: candidate.bedrooms || "",
        bathrooms: candidate.bathrooms || "",
        address_public: "",
      }));
    }
  };

  const publish = async (e) => {
    e?.preventDefault?.();
    if (!publishUnitId) { toast.error("Pick a unit."); return; }
    if (!form.headline || !form.description || !form.rent_cad) {
      toast.error("Headline, description, and rent are required.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/tenant-placement/publish/${publishUnitId}`, {
        headline: form.headline,
        description: form.description,
        rent_cad: parseInt(form.rent_cad, 10),
        bedrooms: form.bedrooms ? parseInt(form.bedrooms, 10) : null,
        bathrooms: form.bathrooms ? parseFloat(form.bathrooms) : null,
        pets: form.pets || null,
        available_on: form.available_on || null,
        address_public: form.address_public || null,
      });
      toast.success("Published! Tenants can now find your listing.");
      setPublishUnitId("");
      setForm({ headline: "", description: "", rent_cad: "", bedrooms: "", bathrooms: "", pets: "", available_on: "", address_public: "" });
      await load();
      // Open the live listing so the landlord sees exactly what tenants see.
      window.open(`/rental/${data.slug}`, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't publish. Please review the form.");
    } finally {
      setSaving(false);
    }
  };

  const unpublish = async (unitId) => {
    if (!window.confirm("Take this listing off the public directory?")) return;
    try {
      await api.delete(`/tenant-placement/publish/${unitId}`);
      toast.success("Listing removed.");
      load();
    } catch { toast.error("Couldn't remove the listing."); }
  };

  const boost = async (unitId) => {
    try {
      await api.post(`/tenant-placement/boost/${unitId}`);
      toast.success("Boosted for 7 days — you now sort above other listings.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't boost this listing.");
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-5 py-10 flex items-center text-slate-500" data-testid="tenant-placement-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading dashboard…
      </div>
    );
  }
  if (paywalled) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-12" data-testid="tenant-placement-paywall">
        <Card className="p-8 bg-amber-50 border-amber-200">
          <div className="text-lg font-bold text-amber-900">Tenant Placement is a subscriber-only feature</div>
          <p className="mt-2 text-sm text-amber-800">Publish your vacant units to Central Rentals&apos; public directory and start receiving enquiries.</p>
          <Link to="/billing" className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white font-semibold text-sm">Open Billing <ArrowRight className="w-4 h-4"/></Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 py-8" data-testid="tenant-placement-page">
      <PageHeader
        title="Tenant Placement"
        subtitle="Listings, enquiries, showings and move-in — connected."
        icon={Rocket}
      />

      <nav className="mt-6 flex flex-wrap items-center gap-3 border-b border-slate-200 pb-3" aria-label="Tenant placement sections">
        <Button data-testid="placement-tab-listings" variant={tab === "listings" ? "default" : "ghost"} onClick={() => setTab("listings")}>Listings</Button>
        <Button data-testid="placement-tab-pipeline" variant={tab === "pipeline" ? "default" : "ghost"} onClick={() => setTab("pipeline")}>Applicant pipeline</Button>
        <Link data-testid="placement-leasing-agent-link" to="/landlord/concierge?tab=directory" className="text-sm font-medium text-emerald-700 sm:ml-auto">Find a local leasing agent</Link>
      </nav>
      {tab === "pipeline" ? <PlacementPipeline/> : <>

      {/* Vacant candidates */}
      {state.vacant_candidates.length > 0 && (
        <Card className="mt-6 p-5" data-testid="tenant-placement-vacants">
          <h3 className="font-display text-lg font-bold text-slate-900">Vacant units you can publish</h3>
          <div className="mt-3 grid md:grid-cols-2 gap-3">
            {state.vacant_candidates.map((v) => (
              <div key={v.unit_id} data-testid={`vacant-${v.unit_id}`} className="p-3 rounded-lg border border-slate-200 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-slate-900 truncate">{v.property_name} · {v.label}</div>
                  <div className="text-xs text-slate-500 truncate">{v.address}</div>
                </div>
                <button
                  type="button"
                  onClick={() => openPublishFor(v.unit_id)}
                  data-testid={`vacant-publish-${v.unit_id}`}
                  className="flex-none px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                >
                  Publish
                </button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Publish form */}
      {publishUnitId && (
        <Card className="mt-6 p-5" data-testid="tenant-placement-form">
          <h3 className="font-display text-lg font-bold text-slate-900 mb-3">Publish this unit</h3>
          <form onSubmit={publish} className="grid md:grid-cols-2 gap-3">
            <FormRow label="Headline" required>
              <input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="e.g. Bright 2-bedroom near Whyte Ave" data-testid="placement-headline" required />
            </FormRow>
            <FormRow label="Monthly rent (CAD)" required>
              <input type="number" min="200" value={form.rent_cad} onChange={(e) => setForm({ ...form, rent_cad: e.target.value })} placeholder="e.g. 1800" data-testid="placement-rent" required />
            </FormRow>
            <FormRow label="Bedrooms">
              <input type="number" min="0" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} placeholder="2" data-testid="placement-bedrooms" />
            </FormRow>
            <FormRow label="Bathrooms">
              <input type="number" min="0" step="0.5" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} placeholder="1.5" data-testid="placement-bathrooms" />
            </FormRow>
            <FormRow label="Available on">
              <input type="date" value={form.available_on} onChange={(e) => setForm({ ...form, available_on: e.target.value })} data-testid="placement-available" />
            </FormRow>
            <FormRow label="Pets">
              <input value={form.pets} onChange={(e) => setForm({ ...form, pets: e.target.value })} placeholder="e.g. Cats OK, no dogs" data-testid="placement-pets" />
            </FormRow>
            <FormRow label="Public address (neighbourhood + city)" full>
              <input value={form.address_public} onChange={(e) => setForm({ ...form, address_public: e.target.value })} placeholder="e.g. Strathcona, Edmonton" data-testid="placement-address" />
            </FormRow>
            <FormRow label="Description" full required>
              <textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Plain-English description. What makes this rental great? What kind of tenant will love it?" data-testid="placement-description" required />
            </FormRow>
            <div className="md:col-span-2 flex items-center gap-2">
              <button type="submit" disabled={saving} data-testid="placement-submit" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold text-sm">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin"/> Publishing…</> : <>Publish listing <ArrowRight className="w-4 h-4"/></>}
              </button>
              <button type="button" data-testid="placement-cancel" onClick={() => setPublishUnitId("")} className="text-xs font-semibold text-slate-500 underline underline-offset-4">Cancel</button>
            </div>
          </form>
        </Card>
      )}

      {/* Published listings */}
      <Card className="mt-6 p-5" data-testid="tenant-placement-published">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold text-slate-900">Your published listings</h3>
          <Link data-testid="placement-public-directory" to="/find-a-place" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-emerald-700 underline underline-offset-4 inline-flex items-center gap-1">
            View public directory <ExternalLink className="w-3 h-3"/>
          </Link>
        </div>
        {state.published.length === 0 ? (
          <p className="text-sm text-slate-500">You haven&apos;t published any listings yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {state.published.map((l) => (
              <li key={l.slug} data-testid={`published-${l.slug}`} className="py-3 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <Link data-testid={`placement-listing-${l.slug}`} to={`/rental/${l.slug}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-sm text-slate-900 hover:text-emerald-700 break-words block">
                    {l.headline}
                  </Link>
                  <div className="text-xs text-slate-500">
                    ${l.rent_cad?.toLocaleString?.() || 0}/mo · {l.city || "—"}
                    {l.boosted_until && <span className="ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100">Boosted</span>}
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3"/> {l.view_count || 0} views</span>
                    <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3"/> {l.enquiry_count || 0} enquiries</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-none">
                  {!l.boosted_until && (
                    <button type="button" onClick={() => boost(l.unit_id)} data-testid={`boost-${l.slug}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold">
                      <TrendingUp className="w-3.5 h-3.5"/> Boost
                    </button>
                  )}
                  <button type="button" onClick={() => unpublish(l.unit_id)} data-testid={`unpublish-${l.slug}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-200 hover:border-rose-400 text-xs font-semibold text-slate-700 hover:text-rose-700">
                    <Trash2 className="w-3.5 h-3.5"/> Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      </>}
    </div>
  );
}

function FormRow({ label, full, required, children }) {
  const className = full ? "md:col-span-2" : "";
  return (
    <label className={`block ${className}`}>
      <div className="text-xs font-bold text-slate-700 mb-1">{label}{required && <span className="text-rose-600 ml-0.5">*</span>}</div>
      <div className="[&_input]:w-full [&_input]:rounded-lg [&_input]:border [&_input]:border-slate-200 [&_input]:px-3 [&_input]:py-2 [&_input]:text-sm [&_textarea]:w-full [&_textarea]:rounded-lg [&_textarea]:border [&_textarea]:border-slate-200 [&_textarea]:px-3 [&_textarea]:py-2 [&_textarea]:text-sm [&_textarea]:resize-none">
        {children}
      </div>
    </label>
  );
}
